import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Card, CardHeader, ContentLoader, Input } from '@/components/common';
import { partnerInviteApi, type ProspectApplication } from '@/services/partnerPortal';

const EMPTY: ProspectApplication = {
  first_name: '',
  last_name: '',
  date_of_birth: '',
  gender: 'male',
  address_line1: '',
  pincode: '',
  pan_number: '',
  aadhaar_number: '',
  bank_name: '',
  bank_account_number: '',
  bank_ifsc_code: '',
  bank_account_holder_name: '',
};

/**
 * Public page behind a one-time invite link: a prospective agent files their application, or a
 * partner / newly approved agent sets a password. The link is the credential; an invalid, used,
 * expired or withdrawn link all look the same on purpose.
 */
export function PartnerInviteAcceptPage() {
  const { token = '' } = useParams<{ token: string }>();
  const info = useQuery({
    queryKey: ['partner-invite', token],
    queryFn: () => partnerInviteApi.info(token),
    retry: false,
  });

  if (info.isLoading) return <ContentLoader />;
  if (info.isError || !info.data) {
    return (
      <Shell title="This link is not valid">
        <p className="text-sm text-gray-600 dark:text-gray-400">It may have expired, been used already, or been withdrawn. Ask the person who invited you for a new one.</p>
      </Shell>
    );
  }
  return info.data.purpose === 'posp_application' ? (
    <ApplicationForm token={token} brokerName={info.data.broker_name} invitedBy={info.data.invited_by} />
  ) : (
    <PasswordForm token={token} brokerName={info.data.broker_name} />
  );
}

function Shell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mx-auto max-w-xl px-4 py-10">
      <Card>
        <CardHeader title={title} />
        {children}
      </Card>
    </div>
  );
}

function PasswordForm({ token, brokerName }: { token: string; brokerName: string }) {
  const [password, setPassword] = useState('');
  const m = useMutation({ mutationFn: () => partnerInviteApi.setPassword(token, password) });
  if (m.isSuccess) {
    return (
      <Shell title="Password set">
        <p className="text-sm text-gray-700 dark:text-gray-300">You can now sign in from the broker login page.</p>
        <a className="mt-3 inline-block text-sm text-primary-600 underline" href="/login/broker">Go to sign in</a>
      </Shell>
    );
  }
  return (
    <Shell title={`Set your password for ${brokerName}`}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
      >
        <Input label="New password" type="password" required autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        {m.isError && <p role="alert" className="text-sm text-red-600 dark:text-red-400">That password was not accepted, or the link is no longer valid. Use a longer password that is not common.</p>}
        <Button type="submit" isLoading={m.isPending}>Set password</Button>
      </form>
    </Shell>
  );
}

function ApplicationForm({ token, brokerName, invitedBy }: { token: string; brokerName: string; invitedBy: string }) {
  const [form, setForm] = useState<ProspectApplication>(EMPTY);
  const m = useMutation({ mutationFn: () => partnerInviteApi.apply(token, form) });
  const set = (k: keyof ProspectApplication) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm({ ...form, [k]: e.target.value });

  if (m.isSuccess) {
    return (
      <Shell title="Application received">
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Thank you. {brokerName} will verify your KYC and review your application. If it is approved you will get a link to set your password.
        </p>
      </Shell>
    );
  }
  return (
    <Shell title={`Join ${brokerName}`}>
      <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">Invited by {invitedBy}. Complete your details below.</p>
      <form
        className="grid gap-4 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
      >
        <Input label="First name" required value={form.first_name} onChange={set('first_name')} />
        <Input label="Last name" required value={form.last_name} onChange={set('last_name')} />
        <Input label="Date of birth" type="date" required value={form.date_of_birth} onChange={set('date_of_birth')} />
        <div>
          <label htmlFor="gender" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Gender</label>
          <select id="gender" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100" value={form.gender} onChange={set('gender')}>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
        </div>
        <Input label="Address" required value={form.address_line1} onChange={set('address_line1')} />
        <Input label="Pincode" required value={form.pincode} onChange={set('pincode')} />
        <Input label="PAN" required maxLength={10} value={form.pan_number} onChange={set('pan_number')} />
        <Input label="Aadhaar (optional)" maxLength={12} inputMode="numeric" value={form.aadhaar_number} onChange={set('aadhaar_number')} />
        <Input label="Bank name" required value={form.bank_name} onChange={set('bank_name')} />
        <Input label="Account holder name" required value={form.bank_account_holder_name} onChange={set('bank_account_holder_name')} />
        <Input label="Account number" required value={form.bank_account_number} onChange={set('bank_account_number')} />
        <Input label="IFSC" required maxLength={11} value={form.bank_ifsc_code} onChange={set('bank_ifsc_code')} />
        {m.isError && <p role="alert" className="text-sm text-red-600 dark:text-red-400 sm:col-span-2">We could not submit this. Check your details (PAN and IFSC formats), or ask the person who invited you for a new link.</p>}
        <div className="sm:col-span-2">
          <Button type="submit" isLoading={m.isPending}>Submit application</Button>
        </div>
      </form>
    </Shell>
  );
}
